import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { formatIrelandTime, toIrelandTime, fromIrelandTime, combineIrelandDateTime } from "../../Utils/timezoneHelper";
import { APIProvider, Map, AdvancedMarker, Pin } from "@vis.gl/react-google-maps";

export default function EditTimesheetModal({ timesheet, user, locations, onClose, onSave }) {
  const [formData, setFormData] = useState({
    startTime: "",
    endTime: "",
    breakDuration: "",
    notes: "",
  });

  useEffect(() => {
    if (timesheet) {
      // Convert UTC to Ireland time for display
      // Use startTime and endTime from timesheet, or fall back to creating datetime-local format
      let startTime = "";
      let endTime = "";
      
      if (timesheet.startTime) {
        const irelandDate = toIrelandTime(timesheet.startTime);
        startTime = `${irelandDate.getFullYear()}-${String(irelandDate.getMonth() + 1).padStart(2, '0')}-${String(irelandDate.getDate()).padStart(2, '0')}T${String(irelandDate.getHours()).padStart(2, '0')}:${String(irelandDate.getMinutes()).padStart(2, '0')}`;
      }
      
      if (timesheet.endTime) {
        const irelandDate = toIrelandTime(timesheet.endTime);
        endTime = `${irelandDate.getFullYear()}-${String(irelandDate.getMonth() + 1).padStart(2, '0')}-${String(irelandDate.getDate()).padStart(2, '0')}T${String(irelandDate.getHours()).padStart(2, '0')}:${String(irelandDate.getMinutes()).padStart(2, '0')}`;
      }
      
      const breakDuration = timesheet.breakDuration ? timesheet.breakDuration : 0;

      setFormData({
        startTime,
        endTime,
        breakDuration: Math.round(breakDuration),
        notes: timesheet.notes || "",
      });
    }
  }, [timesheet]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.startTime || !formData.endTime) {
      toast.error("Clock in and clock out times are required");
      return;
    }

    // Parse Ireland local time from form input
    const irelandStartDate = new Date(formData.startTime);
    const irelandEndDate = new Date(formData.endTime);

    if (irelandEndDate <= irelandStartDate) {
      toast.error("Clock out time must be after clock in time");
      return;
    }

    // Convert Ireland local time to UTC for storage
    const startDateTime = fromIrelandTime(irelandStartDate);
    const endDateTime = fromIrelandTime(irelandEndDate);

    // Calculate totals (using Ireland local times for display)
    const clockTimeMs = irelandEndDate - irelandStartDate;
    const clockTimeHours = clockTimeMs / (1000 * 60 * 60);
    const breakDurationMinutes = parseInt(formData.breakDuration) || 0;
    const breakTimeHours = breakDurationMinutes / 60;
    const totalWorkTime = clockTimeHours - breakTimeHours;

    // Get user's daily work time based on the day of week
    const getDailyWorkTime = () => {
      if (!user) return 8; // Default to 8 hours if no user
      
      // Get day of week (0 = Sunday, 1 = Monday, etc.)
      const dayOfWeek = irelandStartDate.getDay();
      
      // Map day to corresponding work hours field
      const dayMap = {
        0: user.sundayWorkHours || 0,
        1: user.mondayWorkHours || 0,
        2: user.tuesdayWorkHours || 0,
        3: user.wednesdayWorkHours || 0,
        4: user.thursdayWorkHours || 0,
        5: user.fridayWorkHours || 0,
        6: user.saturdayWorkHours || 0,
      };
      
      return dayMap[dayOfWeek] || 8; // Default to 8 if not set
    };
    
    const dailyWorkTime = getDailyWorkTime();
    const overtime = totalWorkTime > dailyWorkTime ? totalWorkTime - dailyWorkTime : 0;

    // Compare with original timesheet to determine which fields actually changed
    // Convert original UTC times to Ireland time for comparison
    const roundToSecond = (date) => Math.floor(date.getTime() / 1000) * 1000;
    
    const originalStartTime = timesheet.startTime ? roundToSecond(toIrelandTime(timesheet.startTime)) : 0;
    const originalEndTime = timesheet.endTime ? roundToSecond(toIrelandTime(timesheet.endTime)) : 0;
    const originalBreakDuration = timesheet.breakDuration || 0;
    const originalNotes = timesheet.notes || "";

    // Check which fields actually changed (comparing Ireland times)
    const startTimeChanged = roundToSecond(irelandStartDate) !== originalStartTime;
    const endTimeChanged = roundToSecond(irelandEndDate) !== originalEndTime;
    const breakDurationChanged = breakDurationMinutes !== originalBreakDuration;
    const notesChanged = formData.notes !== originalNotes;

    const updatedData = {
      startTime: startDateTime.toISOString(), // UTC
      endTime: endDateTime.toISOString(), // UTC
      breakDuration: breakDurationMinutes,
      clockTime: clockTimeHours,
      totalWorkTime: totalWorkTime,
      overtime: overtime,
      notes: formData.notes,
      // Only include fields that actually changed
      editedFields: {
        startTime: startTimeChanged,
        endTime: endTimeChanged,
        breakDuration: breakDurationChanged,
        notes: notesChanged,
      },
      isEdited: startTimeChanged || endTimeChanged || breakDurationChanged || notesChanged,
    };

    onSave(updatedData);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-700">Edit Timesheet</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <i className="fa fa-times"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clock In Time
              </label>
              <input
                type="datetime-local"
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                required
              />
              {/* Clock In Location Map */}
              {timesheet?.clockInLocationID && timesheet.clockInLocationID?.name && timesheet.clockInLocationID?.name !== "UNKNOWN LOCATION" ? (
                <div className="mt-3">
                  <div className="text-xs text-gray-600 mb-2">
                    Location: {timesheet.clockInLocationID.name}
                  </div>
                  {timesheet.clockInLocationID?.latitude && timesheet.clockInLocationID?.longitude ? (
                    <APIProvider apiKey={process.env.REACT_APP_GEO_KEY}>
                      <Map
                        style={{ width: "100%", height: "200px", borderRadius: "8px", border: "1px solid #e5e7eb" }}
                        defaultCenter={{
                          lat: parseFloat(timesheet.clockInLocationID.latitude),
                          lng: parseFloat(timesheet.clockInLocationID.longitude),
                        }}
                        defaultZoom={15}
                        mapId="a8a858ac94fc4bbf59fa3370"
                      >
                        <AdvancedMarker
                          position={{
                            lat: parseFloat(timesheet.clockInLocationID.latitude),
                            lng: parseFloat(timesheet.clockInLocationID.longitude),
                          }}
                        >
                          <Pin background="#4CAF50" borderColor="#333" glyphColor="#fff" />
                        </AdvancedMarker>
                      </Map>
                    </APIProvider>
                  ) : (
                    <div className="text-xs text-gray-400 italic p-4 bg-gray-50 rounded-md">
                      No location coordinates available
                    </div>
                  )}
                </div>
              ) : timesheet?.clockInLatitude && timesheet?.clockInLongitude ? (
                <div className="mt-3">
                  <div className="text-xs text-gray-600 mb-2">
                    Location: UNKNOWN LOCATION
                  </div>
                  <div className="text-xs text-gray-500 mb-2">
                    GPS: {parseFloat(timesheet.clockInLatitude).toFixed(6)}, {parseFloat(timesheet.clockInLongitude).toFixed(6)}
                  </div>
                  <APIProvider apiKey={process.env.REACT_APP_GEO_KEY}>
                    <Map
                      style={{ width: "100%", height: "200px", borderRadius: "8px", border: "1px solid #e5e7eb" }}
                      defaultCenter={{
                        lat: parseFloat(timesheet.clockInLatitude),
                        lng: parseFloat(timesheet.clockInLongitude),
                      }}
                      defaultZoom={15}
                      mapId="a8a858ac94fc4bbf59fa3370"
                    >
                      <AdvancedMarker
                        position={{
                          lat: parseFloat(timesheet.clockInLatitude),
                          lng: parseFloat(timesheet.clockInLongitude),
                        }}
                      >
                        <Pin background="#FF9800" borderColor="#333" glyphColor="#fff" />
                      </AdvancedMarker>
                    </Map>
                  </APIProvider>
                </div>
              ) : (
                <div className="mt-3 text-xs text-red-500 italic p-4 bg-red-50 rounded-md">
                  Clock in location not available
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clock Out Time
              </label>
              <input
                type="datetime-local"
                name="endTime"
                value={formData.endTime}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                required
              />
              {/* Clock Out Location Map */}
              {timesheet?.endTime && timesheet?.clockOutLocationID && timesheet.clockOutLocationID?.name && timesheet.clockOutLocationID?.name !== "UNKNOWN LOCATION" ? (
                <div className="mt-3">
                  <div className="text-xs text-gray-600 mb-2">
                    Location: {timesheet.clockOutLocationID.name}
                  </div>
                  {timesheet.clockOutLocationID?.latitude && timesheet.clockOutLocationID?.longitude ? (
                    <APIProvider apiKey={process.env.REACT_APP_GEO_KEY}>
                      <Map
                        style={{ width: "100%", height: "200px", borderRadius: "8px", border: "1px solid #e5e7eb" }}
                        defaultCenter={{
                          lat: parseFloat(timesheet.clockOutLocationID.latitude),
                          lng: parseFloat(timesheet.clockOutLocationID.longitude),
                        }}
                        defaultZoom={15}
                        mapId="a8a858ac94fc4bbf59fa3370"
                      >
                        <AdvancedMarker
                          position={{
                            lat: parseFloat(timesheet.clockOutLocationID.latitude),
                            lng: parseFloat(timesheet.clockOutLocationID.longitude),
                          }}
                        >
                          <Pin background="#F44336" borderColor="#333" glyphColor="#fff" />
                        </AdvancedMarker>
                      </Map>
                    </APIProvider>
                  ) : (
                    <div className="text-xs text-gray-400 italic p-4 bg-gray-50 rounded-md">
                      No location coordinates available
                    </div>
                  )}
                </div>
              ) : timesheet?.endTime && timesheet?.clockOutLatitude && timesheet?.clockOutLongitude ? (
                <div className="mt-3">
                  <div className="text-xs text-gray-600 mb-2">
                    Location: UNKNOWN LOCATION
                  </div>
                  <div className="text-xs text-gray-500 mb-2">
                    GPS: {parseFloat(timesheet.clockOutLatitude).toFixed(6)}, {parseFloat(timesheet.clockOutLongitude).toFixed(6)}
                  </div>
                  <APIProvider apiKey={process.env.REACT_APP_GEO_KEY}>
                    <Map
                      style={{ width: "100%", height: "200px", borderRadius: "8px", border: "1px solid #e5e7eb" }}
                      defaultCenter={{
                        lat: parseFloat(timesheet.clockOutLatitude),
                        lng: parseFloat(timesheet.clockOutLongitude),
                      }}
                      defaultZoom={15}
                      mapId="a8a858ac94fc4bbf59fa3370"
                    >
                      <AdvancedMarker
                        position={{
                          lat: parseFloat(timesheet.clockOutLatitude),
                          lng: parseFloat(timesheet.clockOutLongitude),
                        }}
                      >
                        <Pin background="#FF9800" borderColor="#333" glyphColor="#fff" />
                      </AdvancedMarker>
                    </Map>
                  </APIProvider>
                </div>
              ) : timesheet?.endTime ? (
                <div className="mt-3 text-xs text-red-500 italic p-4 bg-red-50 rounded-md">
                  Clock out location not available
                </div>
              ) : null}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Break Duration (minutes)
            </label>
            <input
              type="number"
              name="breakDuration"
              value={formData.breakDuration}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
              min="0"
              step="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
              rows="3"
              placeholder="Add notes..."
            />
          </div>

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

